// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Typography, Tooltip, IconButton, Link, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { EnsureGeoidOffsetReason } from '../../elevationThunk';
import { DisplayUnit, toDisplayValue } from '../../cadUnits';
import { PendingGeoidState } from '../../elevationOffset';
import stylesDef from './styles';

// 'pending' = nothing placed yet: a draft's correction is shown but not applied.
export type GeoidStatus = 'resolved' | 'resolving' | 'unavailable' | 'pending';

// One explanation of what the correction is, shared by every state that describes it, so the
// pending and applied tooltips read as the same story told before and after placement.
const GEOID_EXPLANATION = 'It is the gap between the correct geoid your data uses and Esri’s ground surface (WGS84 ellipsoid + terrain). This adjustment accounts for undulation and ensures your data sits correctly on the ground. It never changes your data.';
const NOTHING_APPLIED_YET = 'Nothing is applied yet: your boundary is drawn on the ground as-is.';
// What the adjustment is, for the states that have none to show, so the unavailable and pending
// explanations describe the same thing in the same words.
const ADJUSTMENT_EXPLANATION = 'This is a visualization adjustment we normally make to Esri’s ground surface (WGS84 ellipsoid + terrain) so your data sits flush on the 3D terrain. Your data itself is unchanged.';
// Where every error line sends a reader who is still stuck.
const SUPPORT_EMAIL = 'support@airworks.io';

const magnitude = (valueMetres: number, unit: DisplayUnit, feetPerMetre: number) =>
  `${Math.abs(toDisplayValue(valueMetres, unit, feetPerMetre)).toFixed(2)} ${unit}`;

const signed = (valueMetres: number, unit: DisplayUnit, feetPerMetre: number) => {
  if (valueMetres > 0) return `+${magnitude(valueMetres, unit, feetPerMetre)}`;
  // A proper minus sign (−, U+2212), not the hyphen-minus Number#toFixed would produce for a
  // negative value.
  if (valueMetres < 0) return `−${magnitude(valueMetres, unit, feetPerMetre)}`;
  return magnitude(valueMetres, unit, feetPerMetre);
};

interface ElevationOffsetIconProps {
  // Whether a correction is in force, still being computed, cannot be determined, or is waiting on
  // placement. Picks which explanation below to show.
  geoidStatus: GeoidStatus;
  // The order's automatic geoid correction in metres; only read when geoidStatus is 'resolved'.
  projectGeoidOffset: number;
  // What the draft order can say about its automatic offset; only read when geoidStatus is 'pending'.
  pendingGeoidState: PendingGeoidState;
  feetPerMetre: number;
  displayUnit: DisplayUnit;
}

/**
 * The 'i' explainer for the elevation-offset box, next to its "Elevation Offset" label. States the
 * actual geoid-correction number, so a reader gets the specific value without a separate line for it.
 *
 * A hover Tooltip rather than a click popover: it opens and closes itself as the pointer moves
 * over and off the icon, with no state of its own to manage or click-away to dismiss.
 */
export const ElevationOffsetIcon = ({
  geoidStatus,
  projectGeoidOffset,
  pendingGeoidState,
  feetPerMetre,
  displayUnit,
}: ElevationOffsetIconProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const explanation = (() => {
    if (geoidStatus === 'unavailable') {
      return `No automatic elevation offset was found for this order. ${ADJUSTMENT_EXPLANATION} Set an offset with the Elevation Offset field if the ground surface does not match your data.`;
    }
    if (geoidStatus === 'resolving') {
      return 'Checking whether this order has an automatic elevation offset based on the KML location.';
    }
    if (geoidStatus === 'pending') {
      if (pendingGeoidState.kind === 'no-kml') {
        return `An automatic elevation offset will be computed for this order when a KML is uploaded. ${GEOID_EXPLANATION} It takes effect once your order is placed.`;
      }
      if (pendingGeoidState.kind === 'none') {
        return `No automatic elevation offset was found for this KML. ${ADJUSTMENT_EXPLANATION} Once your order is placed, set an offset with the Elevation Offset field if the ground surface does not match your data.`;
      }
      return `An automatic elevation offset of ${signed(pendingGeoidState.offset, displayUnit, feetPerMetre)} is ready for this order and will be applied for visualization once your order is placed. ${GEOID_EXPLANATION} ${NOTHING_APPLIED_YET}`;
    }
    return `An automatic elevation offset of ${signed(projectGeoidOffset, displayUnit, feetPerMetre)} has been applied for visualization. ${GEOID_EXPLANATION} Use the Elevation Offset field to fine-tune the ground surface further if needed.`;
  })();

  return (
    <Tooltip
      title={explanation}
      placement="top"
      arrow
      enterTouchDelay={0}
      PopperProps={{
        sx: styles.tooltip,
        // Without this, flip/overflow checks are computed against the sidebar's own scrollable
        // container (the nearest clipping ancestor) — but the tooltip itself is portaled outside
        // it, so that boundary doesn't match where it actually renders. Pointing both at the real
        // viewport is what lets it flip to the bottom when there's no room above.
        modifiers: [
          {
            name: 'flip',
            enabled: true,
            options: { fallbackPlacements: ['bottom', 'right', 'left'], rootBoundary: 'viewport' },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: { boundary: 'viewport', altAxis: true, padding: 8 },
          },
        ],
      }}
    >
      <IconButton size="small" sx={styles.infoIconButton} aria-label="About the elevation offset">
        <InfoOutlinedIcon sx={styles.infoIcon} />
      </IconButton>
    </Tooltip>
  );
};

interface ElevationOffsetInfoProps {
  // Whether a correction is in force, still being computed, cannot be determined, or is waiting
  // on placement.
  geoidStatus: GeoidStatus;
  // Why geoidStatus is 'unavailable' — distinguishes "this order has none" (expected) from a
  // genuine failure worth retrying. Ignored unless geoidStatus is 'unavailable'.
  geoidUnavailableReason: EnsureGeoidOffsetReason | null;
  // What the draft order can say about its automatic offset; only read when geoidStatus is 'pending'.
  pendingGeoidState: PendingGeoidState;
  // Whether the user's most recent edit failed to reach the server — the terrain still reflects it
  // for this session, but it will not survive a reload unless they try saving again.
  elevationSaveFailed: boolean;
}

/**
 * A one-line status for the elevation-offset input above: whether a correction is in force, still
 * being checked, or missing — full detail lives behind the 'i' icon in the box header instead of
 * repeating numbers this component doesn't need to know.
 */
export const ElevationOffsetInfo = ({
  geoidStatus,
  geoidUnavailableReason,
  pendingGeoidState,
  elevationSaveFailed,
}: ElevationOffsetInfoProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  // Every error line ends by pointing at support, so the sentence and its mailto link live here
  // once rather than in each branch that needs them.
  const withSupportLine = (text: string) => (
    <>
      {text}
      {' '}
      Need help? Contact
      {' '}
      <Link href={`mailto:${SUPPORT_EMAIL}`} sx={styles.supportLink}>{SUPPORT_EMAIL}</Link>
    </>
  );

  const message = (): React.ReactNode => {
    if (geoidStatus === 'resolving') return 'Checking for an automatic elevation offset…';
    if (geoidStatus === 'resolved') return 'Elevation offset applied. Use the field above to fine-tune the ground surface further if needed.';
    if (geoidStatus === 'pending') {
      if (pendingGeoidState.kind === 'no-kml') return 'Automatic elevation offset is set up when a KML is uploaded.';
      if (pendingGeoidState.kind === 'none') return 'No automatic elevation offset for this KML. Once your order is placed, set an offset if needed.';
      return 'Automatic elevation offset ready. It applies once your order is placed.';
    }
    // 'no-centroid', or no reason at all (a stored 0, the placeholder for "none was calculated"), is
    // the expected case — say so plainly. The other two are transient failures, not a property of
    // the order, so say that instead of repeating the same message for a problem worth retrying.
    if (geoidUnavailableReason === 'lookup-failed') {
      return withSupportLine('Could not set an automatic elevation offset. Reload to retry, or set an offset with the field above.');
    }
    if (geoidUnavailableReason === 'request-failed') {
      return withSupportLine('Could not check for an automatic elevation offset. Reload to retry, or set an offset with the field above.');
    }
    return 'No automatic elevation offset for this order. Set an offset with the field above if needed.';
  };

  const messageStyle = () => {
    if (geoidStatus === 'resolving') return styles.noteResolving;
    if (geoidStatus === 'resolved' || geoidStatus === 'pending') return styles.note;
    // Having no automatic offset is a fact about the order, not a problem: only a lookup or request
    // that failed warrants the warning color.
    const failed = geoidUnavailableReason === 'lookup-failed' || geoidUnavailableReason === 'request-failed';
    return failed ? styles.noteWarning : styles.note;
  };

  return (
    <>
      <Typography sx={messageStyle()}>{message()}</Typography>
      {elevationSaveFailed && (
        <Typography sx={styles.noteWarning}>
          {withSupportLine('Could not save your elevation offset. It applies to this session only. Enter it again to save.')}
        </Typography>
      )}
    </>
  );
};

export default ElevationOffsetInfo;
